<script setup>
import toUpper from 'lodash/toUpper';
import RoleIcons from './RoleIcons.vue';
import TipsHelpful from './TipsHelpful.vue';
import Highlights from './Highlights.vue';
import NerveScore from './NerveScore.vue';
import NeuronsEarned from './NeuronsEarned.vue';
import NTabs from '/app/components/tabs/Tabs.vue';
import NTab from '/app/components/tabs/Tab.vue';
import { useLinks as useAuthLinks, useAuth } from '/app/composables/auth';
import { useFeatureFlag } from '/app/composables/feature-flag';
import { useGameDash } from '/app/composables/gamedash';
import { useGameDashPin } from '/app/composables/gamedash-visibility';
import { track } from '/app/utils/analytics';
import { ref, computed, watch } from 'vue';

const props = defineProps({
    hideHeader: {
        type: Boolean,
        default: false
    },
});
const emit = defineEmits(['update']);
const tab = ref('highlights');
const { loginURL } = useAuthLinks();
const { auth: $auth } = useAuth();
const { brain, role } = useGameDash();
const { enabled: isDevelopmentModeEnabled } = useFeatureFlag('development-mode');
const { pinned, ready: pinReady, toggle: togglePin } = useGameDashPin();

const neuronsScrolled = ref(false);
const neuronsListHeight = computed(() => neuronsScrolled.value ? 'calc(55vh - 3rem)' : 'calc(36vh - 3rem)');

const onNeuronsScroll = (e) => {
    const top = e.target?.scrollTop ?? 0;
    if (top > 40 && !neuronsScrolled.value) neuronsScrolled.value = true;
    else if (top < 8 && neuronsScrolled.value) neuronsScrolled.value = false;
};

// The overlay sub-tabs are client-side only (no URL change), so they are
// invisible to Heap's URL autocapture — track them explicitly.
watch(tab, (current, previous) => {
    if (current === previous) return;

    track('Game Dash Tab Switched', {
        tab: current,
        role: role.value,
    });
});

const onMenuClick = (target) => {
    track('Game Dash Menu Clicked', {
        target,
        role: role.value,
    });
};
</script>

<template>
    <section data-heap-id="gamedash" class="game-dash overflow-hidden" :class="{ 'rounded-tr-2xl': hideHeader}">
        <slot v-if="!hideHeader" name="header">
            <h2 class="game-dash-header m-0">
                <span class="game-dash-header__title">
                    {{ $t('component.game-dash.title') }}
                </span>
            </h2>
        </slot>
        <div class="game-dash__content bg-white !rounded-none">
            <div class="game-dash-menu">
                <RouterLink v-if="$auth.authenticated" :to="{ name: 'dashboard.notifications', params: { platformSlug: $auth.user.screen_name || 'me' } }" class="game-dash-menu__item is-left" data-heap-id="gamedash-menu" data-heap-target="notifications" @click="onMenuClick('notifications')">
                    <span class="game-dash-menu__item-icon icon-brain" aria-hidden="true"></span>
                    All Notifications
                </RouterLink>
            </div>

            <div class="border-x border-300">
                <NTabs v-model="tab" active-class="!bg-white !border-solid !border-t-4 !border-innovator !rounded-none font-bold" class="w-full bg-dust-100" align="between" mode="full">
                    <NTab :title="$t('component.game-dash.highlights.title')" id="highlights">
                        <Highlights class="p-4 mb-1 bg-white sb-snug sb-dust-400 overflow-y-auto" height="calc(55vh - 3rem)" />
                    </NTab>
                    <NTab :title="$t('component.game-dash.neurons-earned.title')" id="neurons-earned" >
                        <div v-if="$auth.authenticated" class="p-4 mb-1 bg-white overflow-hidden" style="height: calc(58vh - 3rem)">
                            <div
                                class="grid transition-[grid-template-rows,opacity,margin-bottom] duration-1000 ease-out"
                                :class="neuronsScrolled ? 'grid-rows-[0fr] opacity-0 mb-0' : 'grid-rows-[1fr] opacity-100 mb-6'"
                            >
                                <div class="overflow-hidden">
                                    <NerveScore
                                        :role="role"
                                        :level="brain?.brain_level"
                                        :position="brain?.rank_position"
                                        :score="brain?.score"
                                        :start-level-score="brain?.start_level_score"
                                        :next-level-score="brain?.next_level_score"
                                        :title="role === 'overall' ? 'Overall Nerve Score' : `${role} Score`"
                                    />
                                </div>
                            </div>
                            <NeuronsEarned :role="role" ref="neurons" :height="neuronsListHeight" @scroll="onNeuronsScroll" />
                        </div>
                        <div v-else class="flex flex-col items-center justify-center pb-20 bg-white" style="height: calc(59vh - 3rem)">
                            <p class="text-dust mb-5">
                                {{ $t('component.game-dash.neurons-earned.sign-in.note') }}
                            </p>
                            <a :href="loginURL" class="flex justify-center !text-white bg-blue hover:bg-blue-600 px-4 py-3 leading-tight rounded-md font-bold whitespace-nowrap">
                                {{ $t('component.game-dash.neurons-earned.sign-in.action') }}
                            </a>
                        </div>
                    </NTab>
                    <NTab :hidden="!isDevelopmentModeEnabled" :title="$t('component.game-dash.helpful-tips.title', { prefix: toUpper(role === 'overall' ? 'Helpful' : role)})" id="tips">
                        <TipsHelpful :role="role" class="p-4 mb-1 bg-white sb-snug sb-dust-400 overflow-y-auto" height="calc(55vh - 3rem)" />
                    </NTab>
                </NTabs>
            </div>

            <div v-if="$auth.authenticated" class="flex justify-start border-x border-300 border-t border-t-dust-200 bg-white px-4 py-2">
                <button type="button" class="flex items-center gap-2 text-sm font-bold text-dust-800 hover:text-dust-900 disabled:opacity-60"
                    :disabled="!pinReady" :aria-pressed="pinned"
                    v-tooltip="{ content: pinned ? 'Remove Game Dash from the right column' : 'Keep Game Dash visible in the right column', popperClass: 'gamedash-pin-tip' }"
                    data-heap-id="gamedash-pin"
                    @click="togglePin({ role })">
                    <span :class="pinned ? 'icon-pin-solid' : 'icon-pin'" aria-hidden="true"></span>
                    {{ pinned ? 'Pinned' : 'Pin to sidebar' }}
                </button>
            </div>
        </div>
        <RoleIcons />
    </section>
</template>
